import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ReactComponent as ArrowPrev } from './arrow_prev.svg';
import { ReactComponent as ArrowNext } from './arrow_next.svg';
import { EmblaOptionsType } from 'embla-carousel';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Autoplay, { AutoplayType } from 'embla-carousel-autoplay';
import { SlideItem } from '../../utils/useBanners';

interface SwipeableTextMobileStepperProps {
    carouselSlides: SlideItem[];
    autoplay?: boolean;
}

export function Carousel(props: SwipeableTextMobileStepperProps) {
    const options: EmblaOptionsType = {
        loop: true,
        duration: 30
    };
    const autoplay: AutoplayType = Autoplay({
        delay: 7000,
        stopOnInteraction: false,
        playOnInit: !!props.autoplay
    });
    const [emblaRef, emblaApi] = useEmblaCarousel(options, [autoplay]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Обновление точек пагинации
    const onInit = useCallback((emblaApi: any) => {
        setScrollSnaps(emblaApi.scrollSnapList());
    }, []);

    const onSelect = useCallback((emblaApi: any) => {
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, []);

    const scrollTo = useCallback(
        (index: number) => {
            return emblaApi && emblaApi.scrollTo(index);
        },
        [emblaApi]
    );

    const scrollPrev = useCallback(() => {
        return emblaApi && emblaApi.scrollPrev();
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
        return emblaApi && emblaApi.scrollNext();
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;

        emblaApi.on('init', onInit);
        emblaApi.on('init', onSelect);
        emblaApi.on('reInit', onInit);
        emblaApi.on('reInit', onSelect);
        emblaApi.on('select', onSelect);
    }, [emblaApi, onInit, onSelect]);

    const slideComponent = (slide: SlideItem, index: number) => {
        const onGameStatusClick = (): void => {
            navigate(slide.href);
        };
        return (
            <div
                key={index}
                className="flex-[0_0_100%] flex flex-col gap-[10px] max-w-full max-tablet:min-w-[192px] max-tablet:min-h-[148px] tablet:min-h-[445px] bg-cover bg-center rounded-[5px] shadow-[0px_2px_4px_0px_rgba(0,0,0,0.2,)]"
                style={{
                    backgroundImage: `url(${slide.imgPath})`
                }}
            >
                <div className="font-medium font-roboto max-tablet:ml-[30px] max-tablet:mt-[30px] max-tablet:text-[18px] tablet:text-[24px] tablet:ml-[120px] tablet:mt-[150px]">
                    {slide.text}
                </div>
                <button
                    className="text-wrap bg-[#FFD73A] hover:bg-[#FFD73A] max-tablet:h-[24px] max-tablet:w-[135px] max-tablet:mb-[30px] mt-auto max-tablet:ml-[30px] rounded-[5px] max-tablet:text-[12px] text-[#000] uppercase font-bold tablet:h-[56px] tablet:w-[230px] tablet:text-[16px] tablet:ml-[120px] tablet:mb-[100px]"
                    onClick={onGameStatusClick}
                >
                    {t('home.slideButton')}
                </button>
            </div>
        );
    };

    return (
        <div className="relative mx-auto w-full max-w-full">
            {/* Основной контейнер карусели */}
            <div
                className="overflow-hidden w-full grid grid-cols-[minmax(0,1fr)]"
                ref={emblaRef}
            >
                <div className="flex">
                    {props.carouselSlides.map(slideComponent)}
                </div>
            </div>

            {/* Кнопки навигации */}
            <button
                className="absolute top-1/2 left-0 flex max-tablet:hidden -translate-y-1/2 transform cursor-pointer items-center justify-end p-2 transition-all duration-200 z-1 w-[80px]"
                onClick={scrollPrev}
                aria-label="Предыдущий слайд"
            >
                <ArrowPrev />
            </button>

            <button
                className="absolute top-1/2 right-0 flex max-tablet:hidden -translate-y-1/2 transform cursor-pointer items-center justify-start p-2 transition-all duration-200 z-1 w-[80px]"
                onClick={scrollNext}
                aria-label="Следующий слайд"
            >
                <ArrowNext />
            </button>

            {/* Пагинация */}
            <div className="absolute w-full bottom-[10px] tablet:bottom-[20px]">
                <div className="flex items-center justify-center">
                    {scrollSnaps.map((_, index) => (
                        <button
                            key={index}
                            className={`rounded-full transition-all duration-200 ${
                                index === selectedIndex
                                    ? 'bg-[#F03D3D] w-[10px] h-[10px] tablet:w-[18px] tablet:h-[18px]'
                                    : 'bg-white/70  w-[8px]  h-[8px]  tablet:h-[16px] tablet:w-[16px]'
                            } cursor-pointer tablet:mx-[9px] rounded-full mx-[6px]`}
                            onClick={() => scrollTo(index)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
